import { useCallback, useEffect, useState } from 'react'
import { Button, Input } from '@heroui/react'
import { api, ApiError } from '../lib/api'
import {
  BREAK_KINDS,
  BREAK_REASON_MAX,
  reasonNameProblem,
  reasonsInOrder,
  type BreakKind,
  type BreakReason,
} from '../lib/orgOptions'
import { explain } from '../lib/staffErrors'
import type { StaffKey } from '../lib/staffText'
import { Icon } from '../ui/icons'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { useFlash } from './sectionKit'
import { ChoiceSelect, Notice, PageHead, SettingBlock, SettingRow, SettingsCard } from './settingsKit'
import '../styles/orgset.css'

/**
 * General (#options): what is the whole organisation's. Its break reasons are one
 * list, so reports compare branches; each branch's switches live on its own page.
 */

type Say = (key: StaffKey, vars?: Record<string, string | number>) => string

const KIND_NAME: Record<BreakKind, StaffKey> = {
  lunch: 'counter.kind.lunch',
  short_break: 'counter.kind.short_break',
  task: 'counter.kind.task',
}

export function Options({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const identity = useSession()?.identity
  const { flash, toast } = useFlash()
  const [reasons, setReasons] = useState<BreakReason[] | null>(null)
  const [loadError, setLoadError] = useState('')

  // The list is written by branch.manage holders who work for every branch.
  const pinned = (identity?.staff.branchIds ?? []).length > 0 && !identity?.staff.owner
  const mayWrite = Boolean(identity?.permissions.includes('branch.manage')) && !pinned

  const load = useCallback(async () => {
    try {
      setReasons((await api.breakReasons(organisation)).reasons)
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 403)) setLoadError(explain(err, lang, 'org.opt.notLoaded'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation])

  useEffect(() => {
    setReasons(null)
    setLoadError('')
    void load()
  }, [load])

  return (
    <>
      <PageHead title={say('settings.general')} line={say('settings.general.line')} />
      {toast}
      {loadError && <Notice>{loadError}</Notice>}
      {reasons && (
        <SettingsCard title={say('org.opt.breaksGroup')}>
          <BreakReasons
            reasons={reasons}
            mayWrite={mayWrite}
            ops={{
              list: async () => (await api.breakReasons(organisation)).reasons,
              create: (body) => api.createBreakReason(body, organisation),
              update: (id, body) => api.updateBreakReason(id, body, organisation),
              remove: (id) => api.deleteBreakReason(id, organisation),
            }}
            onChanged={setReasons}
            flash={flash}
          />
        </SettingsCard>
      )}
    </>
  )
}

/** Where a list of break reasons is read and written: the organisation's, or one branch's own. */
export interface ReasonOps {
  list: () => Promise<BreakReason[]>
  create: (body: { name: string; kind?: BreakKind; sortOrder?: number }) => Promise<unknown>
  update: (id: string, body: { name?: string; kind?: BreakKind; sortOrder?: number }) => Promise<unknown>
  remove: (id: string) => Promise<unknown>
}

/**
 * A list of break reasons, edited in place: rename on Enter or leaving the box,
 * kind from a picker, order by the arrows. Each change is its own call. taken
 * are names the server would refuse besides the list's own.
 */
export function BreakReasons({
  reasons,
  ops,
  mayWrite,
  onChanged,
  flash,
  taken = [],
  heading = true,
}: {
  reasons: BreakReason[]
  ops: ReasonOps
  mayWrite: boolean
  onChanged: (next: BreakReason[]) => void
  flash: (text: string) => void
  taken?: readonly BreakReason[]
  heading?: boolean
}) {
  const { lang, say } = useStaffText()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<BreakKind>('short_break')
  const ordered = reasonsInOrder(reasons)
  const newProblem = name.trim() ? reasonNameProblem(name, [...reasons, ...taken]) : ''

  const reload = async () => onChanged(await ops.list())
  const act = async (fn: () => Promise<unknown>, said?: string) => {
    setBusy(true)
    setError('')
    try {
      await fn()
      await reload()
      if (said) flash(said)
      return true
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      return false
    } finally {
      setBusy(false)
    }
  }

  // Swapping two neighbours' places, or renumbering when they share one.
  const move = (r: BreakReason, by: -1 | 1) => {
    const i = ordered.findIndex((x) => x.id === r.id)
    const other = ordered[i + by]
    if (!other) return
    const tied = other.sortOrder === r.sortOrder
    void act(async () => {
      if (tied) {
        const next = [...ordered]
        next.splice(i, 1)
        next.splice(i + by, 0, r)
        for (const [n, x] of next.entries()) {
          if (x.sortOrder !== (n + 1) * 10) await ops.update(x.id, { sortOrder: (n + 1) * 10 })
        }
      } else {
        await ops.update(r.id, { sortOrder: other.sortOrder })
        await ops.update(other.id, { sortOrder: r.sortOrder })
      }
    }, say('record.orderSaved'))
  }

  const add = () => {
    if (!name.trim() || newProblem || busy) return
    void act(
      () => ops.create({ name: name.trim(), kind, sortOrder: (ordered.at(-1)?.sortOrder ?? 0) + 10 }),
      say('org.opt.reasonAdded'),
    ).then((ok) => {
      if (ok) setName('')
    })
  }

  return (
    <>
    {heading && <SettingRow label={say('org.opt.breakReasons')} description={say('org.opt.breakReasonsHint')} />}
    <SettingBlock>
    <div className="os-reasons">
      {ordered.length === 0 ? (
        <p className="os-help">{say('org.opt.noReasons')}</p>
      ) : (
        <ul className="os-items__list">
          {ordered.map((r, i) => (
            <ReasonRow
              key={`${r.id}:${r.name}:${r.kind}`}
              reason={r}
              others={[...reasons, ...taken]}
              first={i === 0}
              last={i === ordered.length - 1}
              busy={busy}
              mayWrite={mayWrite}
              say={say}
              onRename={(next) => void act(() => ops.update(r.id, { name: next }), say('record.saved'))}
              onKind={(next) => void act(() => ops.update(r.id, { kind: next }), say('record.saved'))}
              onMove={(by) => move(r, by)}
              onDelete={() => void act(() => ops.remove(r.id), say('org.opt.reasonDeleted'))}
            />
          ))}
        </ul>
      )}
      {mayWrite && (
        <form
          className="os-items__add"
          onSubmit={(e) => {
            e.preventDefault()
            add()
          }}
        >
          <Input
            value={name}
            maxLength={BREAK_REASON_MAX + 1}
            aria-label={say('org.opt.reasonNew')}
            placeholder={say('org.opt.reasonNew')}
            aria-invalid={Boolean(newProblem) || undefined}
            onChange={(e) => setName(e.target.value)}
          />
          <span className="os-items__kind">
            <ChoiceSelect
              label={say('org.opt.reasonKind')}
              value={kind}
              onChange={(next) => setKind(next as BreakKind)}
              options={BREAK_KINDS.map((k) => ({ value: k, label: say(KIND_NAME[k]) }))}
            />
          </span>
          <Button type="submit" variant="secondary" isDisabled={busy || !name.trim() || Boolean(newProblem)}>
            <Icon name="plus" size={16} /> {say('org.opt.reasonAdd')}
          </Button>
        </form>
      )}
      {newProblem && <p className="os-items__error">{say(newProblem === 'taken' ? 'org.opt.reasonTaken' : 'org.opt.reasonLong')}</p>}
      {error && <Notice>{error}</Notice>}
    </div>
    </SettingBlock>
    </>
  )
}

function ReasonRow({
  reason,
  others,
  first,
  last,
  busy,
  mayWrite,
  say,
  onRename,
  onKind,
  onMove,
  onDelete,
}: {
  reason: BreakReason
  others: BreakReason[]
  first: boolean
  last: boolean
  busy: boolean
  mayWrite: boolean
  say: Say
  onRename: (name: string) => void
  onKind: (kind: BreakKind) => void
  onMove: (by: -1 | 1) => void
  onDelete: () => void
}) {
  const [name, setName] = useState(reason.name)
  const problem = reasonNameProblem(name, others, reason.id)
  const commit = () => {
    if (name.trim() === reason.name) return
    if (problem) setName(reason.name)
    else onRename(name.trim())
  }
  return (
    <li className="os-items__row">
      <Input
        variant="secondary"
        value={name}
        maxLength={BREAK_REASON_MAX + 1}
        disabled={!mayWrite || busy}
        aria-label={say('org.opt.reasonName')}
        aria-invalid={Boolean(problem) || undefined}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          if (e.key === 'Escape') setName(reason.name)
        }}
      />
      <span className="os-items__kind">
        <ChoiceSelect
          size="sm"
          label={say('org.opt.reasonKind')}
          value={reason.kind}
          disabled={!mayWrite || busy}
          onChange={(next) => next !== reason.kind && onKind(next as BreakKind)}
          options={BREAK_KINDS.map((k) => ({ value: k, label: say(KIND_NAME[k]) }))}
        />
      </span>
      {mayWrite && (
        <span className="os-items__acts">
          <Button isIconOnly variant="ghost" size="sm" isDisabled={first || busy} aria-label={say('org.opt.reasonUp', { name: reason.name })} onPress={() => onMove(-1)}>
            <Icon name="up" size={16} />
          </Button>
          <Button isIconOnly variant="ghost" size="sm" isDisabled={last || busy} aria-label={say('org.opt.reasonDown', { name: reason.name })} onPress={() => onMove(1)}>
            <Icon name="down" size={16} />
          </Button>
          <Button isIconOnly variant="ghost" size="sm" isDisabled={busy} aria-label={say('org.opt.reasonDelete', { name: reason.name })} onPress={onDelete}>
            <Icon name="close" size={16} />
          </Button>
        </span>
      )}
    </li>
  )
}

